import React from 'react';
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
  XAxis
} from 'recharts';

import {
  getLatestYear,
  getTrendSummary,
  getVisibleYears,
  getYearCountLabel,
  parseNumericValue
} from '../../shared/historyUtils.js';

function CustomTooltip({ active, payload, yearData }) {
  if(active && payload && payload[0]) {
    const year = payload[0].payload.year;
    return (
      <div className="metric-card-tooltip">
        <span className="metric-card-tooltip-year">{year}</span>
        <strong className="metric-card-tooltip-value">{yearData[year]}</strong>
      </div>
    );
  }

  return null;
}

function getWindowLabel(historyWindow) {
  switch(historyWindow) {
    case '5y':
      return 'Last 5 years';
    case '10y':
      return 'Last 10 years';
    case 'all':
      return 'All years';
    case 'recent':
    default:
      return 'Recent years';
  }
}

function MetricCard({ name, yearData, historyWindow = 'recent', compact = false }) {
  if(!yearData) return null;

  const latestYear = getLatestYear(yearData);
  const latestValue = latestYear ? yearData[latestYear] : 'N/A';
  const visibleYears = getVisibleYears(yearData, historyWindow);
  const chartData = visibleYears.map((year) => ({
    year,
    value: parseNumericValue(yearData[year])
  })).filter((item) => item.value !== null);
  const hasChart = chartData.length > 1;
  const trend = getTrendSummary(yearData, visibleYears);
  const cardClassName = compact ? 'metric-card metric-card-compact' : 'metric-card';

  return (
    <div className={cardClassName}>
      <div className="metric-card-top">
        <div className="metric-card-label">{name}</div>
        <span className="metric-card-pill">{getYearCountLabel(yearData)}</span>
      </div>

      <div className="metric-card-value">{latestValue}</div>

      <div className="metric-card-foot">
        <span className="metric-card-year">Latest: {latestYear || 'N/A'}</span>
        <span className="metric-card-window">{getWindowLabel(historyWindow)}</span>
      </div>

      {trend && (
        <div className={trend.direction === 'up' ? 'metric-card-trend trend-up' : 'metric-card-trend trend-down'}>
          {trend.label}
        </div>
      )}

      {hasChart ? (
        <div className="metric-card-chart metric-card-chart-lg">
          <ResponsiveContainer width="100%" height={compact ? 72 : 92}>
            <AreaChart data={chartData} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="metricCardGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2F80ED" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#2F80ED" stopOpacity={0.03} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="year"
                tick={{ fontSize: compact ? 9 : 10, fill: '#7b8ca1' }}
                axisLine={false}
                tickLine={false}
                minTickGap={8}
              />
              <Tooltip
                content={<CustomTooltip yearData={yearData} />}
                cursor={{ stroke: 'rgba(47,128,237,0.18)', strokeWidth: 1 }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#2F80ED"
                strokeWidth={2.2}
                fill="url(#metricCardGradient)"
                dot={{ r: 2.2, fill: '#2F80ED', strokeWidth: 0 }}
                activeDot={{ r: 4, fill: '#1f5fb5', stroke: '#ffffff', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="metric-card-empty">Single year available</div>
      )}
    </div>
  );
}

export default MetricCard;
